import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, Phone, Mail } from 'lucide-react';
import SEOHead from '../seo/SEOHead';
import { ServiceSchema, BreadcrumbSchema } from '../seo/SchemaMarkup';
import { PAGE_SEO, SITE_URL, SERVICES_SEO } from '../../seoConfig';

// Map slugs to SEO keys
const slugToKey = {
  'fassadensanierung-stuttgart': 'fassadensanierung',
  'dachsanierung-stuttgart': 'dachsanierung',
  'innensanierung-stuttgart': 'innensanierung',
  'energetische-sanierung-stuttgart': 'energetischeSanierung'
};

const LeistungDetail = () => {
  const { slug } = useParams();
  const serviceKey = slugToKey[slug];
  
  // Redirect if service not found
  if (!serviceKey || !SERVICES_SEO[serviceKey]) {
    return <Navigate to="/leistungen" replace />;
  }
  
  const service = SERVICES_SEO[serviceKey];
  const pageSeo = PAGE_SEO[serviceKey];
  
  const breadcrumbs = [
    { name: "Start", url: SITE_URL },
    { name: "Leistungen", url: `${SITE_URL}/leistungen` },
    { name: service.name, url: `${SITE_URL}/leistungen/${slug}` }
  ];

  // Get other services for "Weitere Leistungen" section
  const otherServices = Object.entries(SERVICES_SEO)
    .filter(([key]) => key !== serviceKey)
    .slice(0, 3)
    .map(([, value]) => value);

  // Parse the long description into sections
  const descriptionParts = service.longDescription.split('\n\n');
  const introText = descriptionParts[0];
  const listSection = descriptionParts[1] || '';
  const listItems = listSection.split('\n').filter(item => item.startsWith('•')).map(item => item.replace('• ', ''));

  return (
    <>
      <SEOHead 
        title={pageSeo.title}
        description={pageSeo.description}
        canonical={pageSeo.canonical}
      />
      <ServiceSchema service={service} />
      <BreadcrumbSchema items={breadcrumbs} />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-24">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: `url(${service.image})` }}
          ></div>
          <div className="container mx-auto px-6 relative z-10">
            {/* Breadcrumb */}
            <nav className="text-sm mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2 flex-wrap">
                <li><Link to="/" className="text-gray-400 hover:text-white">Start</Link></li>
                <li className="text-gray-500">/</li>
                <li><Link to="/leistungen" className="text-gray-400 hover:text-white">Leistungen</Link></li>
                <li className="text-gray-500">/</li>
                <li className="text-[#DC2626]">{service.name.replace(' in Stuttgart', '')}</li>
              </ol>
            </nav>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {pageSeo.h1}
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              {service.shortDescription}
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
                  <img 
                    src={service.image}
                    alt={service.imageAlt}
                    className="w-full h-64 md:h-96 object-cover rounded-xl mb-8"
                  />
                  
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 text-lg leading-relaxed mb-8">
                      {introText}
                    </p>
                    
                    {listItems.length > 0 && (
                      <>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">
                          Unsere Leistungen im Detail
                        </h2>
                        <ul className="space-y-4">
                          {listItems.map((item, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <CheckCircle className="w-6 h-6 text-[#DC2626] flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>

                {/* Back Link */}
                <Link 
                  to="/leistungen"
                  className="inline-flex items-center gap-2 mt-8 text-gray-600 hover:text-[#DC2626] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Zurück zur Übersicht
                </Link>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                {/* Contact CTA */}
                <div className="bg-[#DC2626] rounded-2xl p-8 text-white mb-8 sticky top-24">
                  <h3 className="text-2xl font-bold mb-4">Interesse geweckt?</h3>
                  <p className="text-white/90 mb-6">
                    Kontaktieren Sie uns für eine unverbindliche Besichtigung und ein kostenloses Angebot.
                  </p>
                  <div className="space-y-4">
                    <a 
                      href="tel:+491729308274"
                      className="flex items-center gap-3 bg-white/20 hover:bg-white/30 rounded-lg p-4 transition-colors"
                    >
                      <Phone className="w-5 h-5" />
                      <span>0172 9308274</span>
                    </a>
                    <a 
                      href="mailto:hk-altbausanierung@outlook.de"
                      className="flex items-center gap-3 bg-white/20 hover:bg-white/30 rounded-lg p-4 transition-colors"
                    >
                      <Mail className="w-5 h-5" />
                      <span>E-Mail schreiben</span>
                    </a>
                    <Link 
                      to="/kontakt"
                      className="flex items-center justify-center gap-2 bg-white text-[#DC2626] rounded-lg p-4 font-bold hover:bg-gray-100 transition-colors"
                    >
                      Kontaktformular <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

                {/* Other Services */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Weitere Leistungen</h3>
                  <div className="space-y-3">
                    {otherServices.map((otherService, index) => (
                      <Link 
                        key={index}
                        to={`/leistungen/${otherService.slug}`}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                      >
                        <img 
                          src={otherService.image}
                          alt={otherService.imageAlt}
                          className="w-16 h-16 object-cover rounded-lg"
                          loading="lazy"
                        />
                        <span className="font-medium text-gray-700 group-hover:text-[#DC2626] transition-colors">
                          {otherService.name.replace(' in Stuttgart', '')}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LeistungDetail;
